import { useCallback, useEffect, useState } from 'react';
import monthlyReportService from '../auditor/service/monthlyReportService.mjs';
import { getReportStatus, getReportClaimStatus } from '../utils/statusConfig.mjs';

/**
 * Read-only viewer for an archived version of a monthly report.
 *
 * The approval chain has always stored a snapshot of the report at each stage,
 * but nothing served it - so the report page could display "Version 3" and
 * offer no way to see what version 1 or 2 contained. This drawer is that.
 *
 * HONESTY ABOUT MISSING DATA IS THE POINT OF THIS COMPONENT.
 *
 * Snapshots were written in four different shapes over the system's life and
 * some of them lost figures. The server classifies each one (`fidelity`) and
 * this renders accordingly: a money cell whose value was never captured shows a
 * dash and the drawer says why, rather than showing 0.00 - which would look
 * exactly like a real zero and is how the original data loss stayed invisible.
 */

const FIDELITY_TONE = {
  full: 'bg-emerald-50 border-emerald-200 text-emerald-900',
  partial: 'bg-amber-50 border-amber-200 text-amber-900',
  claims_no_amounts: 'bg-orange-50 border-orange-300 text-orange-900',
  metadata_only: 'bg-gray-100 border-gray-300 text-gray-700',
};

const money = (v) =>
  v === null || v === undefined
    ? null
    : `$${Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/** A money cell that refuses to invent a figure. */
const Money = ({ value }) => {
  const text = money(value);
  return text
    ? <span className="tabular-nums">{text}</span>
    : <span className="text-gray-400" title="Not captured in this version">-</span>;
};

const when = (v) => {
  if (!v) return '-';
  const d = new Date(v);
  return Number.isNaN(d.getTime())
    ? '-'
    : d.toLocaleString('en-US', {
        year: 'numeric', month: 'short', day: '2-digit', hour: 'numeric', minute: '2-digit',
      });
};

const ReportVersionViewer = ({ reportId, onClose }) => {
  const [index, setIndex] = useState(null);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState(null);

  // Close on Escape, like the other overlays in the app.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    monthlyReportService.getReportVersions(reportId)
      .then((data) => {
        if (cancelled) return;
        setIndex(data);
        // Open on the newest version that actually archived something.
        const first = (data?.versions || [])[0];
        if (first) setSelected(first.version);
      })
      .catch((e) => { if (!cancelled) setError(e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [reportId]);

  const loadVersion = useCallback((version) => {
    setSelected(version);
    setDetailLoading(true);
    setDetail(null);
    monthlyReportService.getReportVersion(reportId, version)
      .then((d) => setDetail(d))
      .catch((e) => setError(e.message))
      .finally(() => setDetailLoading(false));
  }, [reportId]);

  useEffect(() => {
    if (selected != null) loadVersion(selected);
    // loadVersion is stable per reportId; selected drives this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, reportId]);

  const versions = index?.versions || [];

  return (
    <div className="fixed inset-0 z-[10000] bg-black/50 flex items-stretch justify-end" onClick={onClose}>
      <div
        className="bg-gray-50 w-full max-w-5xl h-full overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Report version history"
      >
        <div className="sticky top-0 bg-gray-900 text-white px-4 py-4 sm:px-6 flex items-center justify-between gap-3 z-10">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold">Version History</h2>
            <p className="text-gray-300 text-sm">
              {index ? `${index.region} · ${index.month}/${index.year} · currently v${index.currentVersion}` : 'Loading...'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="flex-shrink-0 min-h-[44px] sm:min-h-0 px-4 sm:px-3 py-1.5 text-sm bg-white/10 hover:bg-white/20 rounded-lg"
          >
            Close
          </button>
        </div>

        {loading && (
          <div className="p-10 text-center text-gray-500">Loading versions...</div>
        )}

        {error && (
          <div className="m-4 sm:m-6 bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-red-800">
            {error}
          </div>
        )}

        {!loading && !error && versions.length === 0 && (
          <div className="m-4 sm:m-6 bg-gray-100 border border-gray-300 rounded-lg px-4 py-3 text-gray-700">
            No archived versions were recorded for this report.
          </div>
        )}

        {!loading && versions.length > 0 && (
          <div className="p-4 sm:p-6 space-y-4">
            {/* Version picker */}
            <div className="flex flex-wrap gap-2">
              {versions.map((v) => (
                <button
                  key={v.version}
                  onClick={() => setSelected(v.version)}
                  aria-pressed={selected === v.version}
                  className={`px-3 py-2 rounded-lg text-sm border transition-colors ${
                    selected === v.version
                      ? 'bg-stanford-red text-white border-stanford-red'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-100'
                  }`}
                >
                  <span className="font-semibold">v{v.version}</span>
                  {v.isCurrent && <span className="ml-1.5 text-xs opacity-80">current</span>}
                  <span className="block text-xs opacity-75 mt-0.5">
                    {v.stages.length} event{v.stages.length === 1 ? '' : 's'}
                  </span>
                </button>
              ))}
            </div>

            {detailLoading && <div className="text-gray-500 text-sm">Loading version...</div>}

            {detail && (
              <>
                {/* What this archive is, and how complete it is. Always shown -
                    including for 'full', so the reader knows the figures are as
                    at the capture date and not today's. */}
                <div className={`border rounded-lg px-4 py-3 ${FIDELITY_TONE[detail.fidelity] || FIDELITY_TONE.metadata_only}`}>
                  <p className="font-semibold text-sm">
                    Archived version {detail.version}
                    {detail.isCurrent && ' (current)'} - read-only
                  </p>
                  <p className="text-sm mt-0.5">
                    Captured {when(detail.capturedAt)} at &ldquo;{detail.capturedAtStageLabel}&rdquo; by {detail.capturedBy}.
                  </p>
                  {detail.note && <p className="text-sm mt-1.5 font-medium">{detail.note}</p>}
                </div>

                {/* Report header as it stood */}
                <div className="bg-white rounded-lg border border-gray-200 p-4 grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500 block text-xs">Region</span>
                    <span className="font-medium">{detail.report.region || '-'}</span>
                  </div>
                  <div>
                    <span className="text-gray-500 block text-xs">Period</span>
                    <span className="font-medium">
                      {detail.report.month && detail.report.year
                        ? `${detail.report.month}/${detail.report.year}`
                        : '-'}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500 block text-xs">
                      {detail.fidelity === 'metadata_only' || detail.fidelity === 'claims_no_amounts'
                        ? 'Report total at capture'
                        : 'Total'}
                    </span>
                    <span className="font-medium"><Money value={detail.report.totalAmount} /></span>
                  </div>
                  <div>
                    <span className="text-gray-500 block text-xs">Status at capture</span>
                    {detail.report.status
                      ? (
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${getReportStatus(detail.report.status, detail.report).badge}`}>
                          {getReportStatus(detail.report.status, detail.report).label}
                        </span>
                      )
                      : <span className="text-gray-400">-</span>}
                  </div>
                </div>

                {/* Claims as they stood */}
                {detail.claims.length > 0 ? (
                  <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="responsive-table w-full text-sm">
                        <thead className="bg-gray-100 text-gray-700">
                          <tr>
                            <th className="px-3 py-2 text-left font-semibold">#</th>
                            <th className="px-3 py-2 text-left font-semibold">Claimant</th>
                            <th className="px-3 py-2 text-left font-semibold">Type</th>
                            <th className="px-3 py-2 text-right font-semibold">Per Diem</th>
                            <th className="px-3 py-2 text-right font-semibold">Reimbursed</th>
                            <th className="px-3 py-2 text-right font-semibold">Total</th>
                            <th className="px-3 py-2 text-left font-semibold">PTAE</th>
                            <th className="px-3 py-2 text-left font-semibold">Line status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {detail.claims.map((c, i) => (
                            <tr key={`${c.claimId}-${i}`} className="border-t border-gray-100">
                              <td className="hidden md:table-cell px-3 py-2 text-gray-500">{i + 1}</td>
                              <td data-cell="title" className="px-3 py-2">
                                {c.userName || <span className="text-gray-400">not captured</span>}
                              </td>
                              <td data-label="Type" className="px-3 py-2">
                                {c.claimType === 'ExpenseClaim' ? 'Expense' : 'Per Diem'}
                              </td>
                              <td data-label="Per Diem" className="px-3 py-2 text-right"><Money value={c.perDiem} /></td>
                              <td data-label="Reimbursed" className="px-3 py-2 text-right"><Money value={c.reimbursements} /></td>
                              <td data-label="Total" className="px-3 py-2 text-right font-medium"><Money value={c.total} /></td>
                              <td data-label="PTAE" className="px-3 py-2 font-mono text-xs max-md:before:font-sans">
                                {c.ptae || <span className="text-gray-400 font-sans">-</span>}
                              </td>
                              <td data-label="Line status" className="px-3 py-2">
                                {/* One wrapper so the badge and flag reason stay
                                    stacked beside the label on a phone card. */}
                                <div className="min-w-0">
                                {c.status
                                  ? (
                                    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs ${getReportClaimStatus(c.status).badge}`}>
                                      {getReportClaimStatus(c.status).label}
                                    </span>
                                  )
                                  : <span className="text-gray-400">-</span>}
                                {c.flagged && c.flagReason && (
                                  <p className="text-xs text-orange-800 mt-1">{c.flagReason}</p>
                                )}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : (
                  <div className="bg-white rounded-lg border border-gray-200 px-4 py-6 text-sm text-gray-600">
                    {detail.report.claimsCount != null
                      ? `This version recorded ${detail.report.claimsCount} claim${detail.report.claimsCount === 1 ? '' : 's'}, but not their individual lines.`
                      : 'No claim lines were archived for this version.'}
                  </div>
                )}

                {/* What happened during this version */}
                <div className="bg-white rounded-lg border border-gray-200 p-4">
                  <h3 className="font-semibold text-gray-800 text-sm mb-2">
                    Events in version {detail.version}
                  </h3>
                  <ol className="space-y-2">
                    {detail.stages.map((s, i) => (
                      <li key={i} className="text-sm border-l-2 border-gray-200 pl-3">
                        <span className="font-medium text-gray-800">{s.label}</span>
                        <span className="text-gray-500"> · {when(s.performedAt)}</span>
                        {s.comments && <p className="text-gray-600 mt-0.5">{s.comments}</p>}
                      </li>
                    ))}
                  </ol>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ReportVersionViewer;
